import { useEffect, useState } from 'react';
import type { Device } from '@glyph/protocol-ts';
import { useProfiles, usePushLook } from '@glyph/core';
import { Checkbox, Dialog, Field, Note, Select, useToast } from '@glyph/ui';

export interface LookDialogProps {
  open: boolean;
  onClose: () => void;
  /** Every unit on the page: any online one can be the source. */
  devices: Device[];
  /** The ticked units: they receive the look (the source itself is skipped). */
  targets: Device[];
}

/**
 * Fleet "Apply look": copy one unit's caption look (font, colour, position, box, mode) to the ticked units — its
 * current look or one of its saved presets, optionally kept as a preset of the same name on each unit. The cloud reads
 * the source and fans the display section out through the queueable command bus, so offline units get it on reconnect.
 */
export function LookDialog({ open, onClose, devices, targets }: LookDialogProps) {
  const online = devices.filter((d) => d.online);
  const [sourceId, setSourceId] = useState('');
  useEffect(() => {
    if (open) setSourceId((targets.find((d) => d.online) ?? online[0])?.id ?? '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  if (!open) return null;
  const source = online.find((d) => d.id === sourceId);
  if (!source) {
    return (
      <Dialog open onClose={onClose} title="Apply look" cancelLabel="Close">
        <Note tone="warn">No unit is online to copy a look from. The source unit has to be reachable so the console can read its current settings.</Note>
      </Dialog>
    );
  }
  return <LookForm key={source.id} source={source} online={online} targets={targets} onSource={setSourceId} onClose={onClose} />;
}

function LookForm({
  source,
  online,
  targets,
  onSource,
  onClose,
}: {
  source: Device;
  online: Device[];
  targets: Device[];
  onSource: (id: string) => void;
  onClose: () => void;
}) {
  const profiles = useProfiles(source.id);
  const push = usePushLook();
  const toast = useToast();
  const [preset, setPreset] = useState('');
  const [keep, setKeep] = useState(true);
  const names = profiles.data ?? [];
  const others = targets.filter((d) => d.id !== source.id);
  const offline = others.filter((d) => !d.online).length;
  const sourceOptions = online.map((d) => ({ value: d.id, label: d.name }));
  const presetOptions = [{ value: '', label: `Current look on ${source.name}` }, ...names.map((n) => ({ value: n, label: `Preset · ${n}` }))];
  const submit = async () => {
    if (!others.length) return;
    try {
      const r = await push.push({
        source_device_id: source.id,
        preset: preset || null,
        save_as: preset && keep ? preset : null,
        device_ids: others.map((d) => d.id),
      });
      const sent = r.results.filter((x) => x.status === 'sent').length;
      const queued = r.results.filter((x) => x.status === 'queued').length;
      const failed = r.results.filter((x) => x.status === 'error' || x.status === 'forbidden');
      const kept = r.results.filter((x) => x.saved).length;
      toast.push(
        `Look from ${source.name}${preset ? ` (${preset})` : ''}: ${sent} applied${queued ? `, ${queued} queued until online` : ''}${
          preset && keep ? `, kept as a preset on ${kept}` : ''
        }${failed.length ? `, ${failed.length} failed` : ''}`,
        failed.length ? 'warn' : 'ok',
      );
      onClose();
    } catch (e) {
      toast.push((e as Error).message || 'Could not apply the look', 'bad');
    }
  };
  return (
    <Dialog
      open
      onClose={onClose}
      title={`Apply look to ${others.length} unit${others.length === 1 ? '' : 's'}`}
      meta={`FROM ${source.name.toUpperCase()} · OFFLINE UNITS GET IT ON RECONNECT`}
      confirm={{ label: 'Apply look', onClick: submit, disabled: !others.length || push.isPending, busy: push.isPending }}
    >
      <div className="flex flex-col gap-3">
        <Field label="Copy from">
          <Select block value={source.id} onChange={onSource} options={sourceOptions} aria-label="Source unit" />
        </Field>
        <Field label="Look">
          <Select block value={preset} onChange={setPreset} options={presetOptions} aria-label="Look" disabled={profiles.isLoading} />
        </Field>
        {preset && (
          <div className="flex items-center gap-2 text-base">
            <Checkbox checked={keep} onChange={setKeep} label={`Keep as preset ${preset} on each unit`} />
            <span>Keep it as preset “{preset}” on each unit</span>
          </div>
        )}
        <Note>
          Font, colour, position, box and caption mode travel. Audio, Deepgram and output assignment stay as they are on each unit.
          {preset && keep ? ' Units on older software just get the look applied.' : ''}
        </Note>
        {others.length ? (
          <Note>
            {others.map((d) => d.name).join(', ')}
            {offline ? ` · ${offline} offline` : ''}
          </Note>
        ) : (
          <Note tone="warn">Tick at least one other unit: {source.name} already has this look.</Note>
        )}
      </div>
    </Dialog>
  );
}
